import {
  AdminDashboardApi,
  AdminManagementApi,
  AuthenticationApi,
  CouncilAdminInvitationsApi,
  CouncilAlarmApi,
  CouncilAdminApi,
  CouncilApi,
  CouncilAppVersionApi,
  CouncilConsumptionApi,
  CouncilCustomerApi,
  CouncilDashboardApi,
  CouncilDeviceApi,
  CouncilReportApi,
  MeterCatalogControllerApi,
  StatisticsControllerApi,
  SuperAdminAnalyticsControllerApi,
  SuperAdminCouncilControllerApi,
  UserProfileApi,
  WaterbureauRegistrationApi,
  Configuration,
} from "@/gen/api"
import type { AxiosRequestConfig, AxiosError } from "axios"
import globalAxios from "axios"
import { authStore } from "@/stores/authStore"

type RetriableAxiosRequestConfig = AxiosRequestConfig & {
  _retry?: boolean
}

export const BASE_PATH =
  import.meta.env["VITE_API_BASE_URL"] ?? "https://api.admin-portal.waterbureau.com.au"

const apiConfig = new Configuration({
  basePath: BASE_PATH,
})

export const setRouterInstance = (_router: { navigate: (path: string) => void }) => {
  // kept for API compatibility
}

let isRefreshing = false
let hasLoggedOut = false

export const resetForceLogout = () => {
  hasLoggedOut = false
}

const isRefreshRequest = (config?: AxiosRequestConfig): boolean => {
  if (!config) return false
  const url = config.url ?? ""
  return url.includes("/api/auth/refresh")
}
let failedQueue: Array<{
  resolve: (value?: unknown) => void
  reject: (error: unknown) => void
  originalRequest: AxiosRequestConfig
}> = []

const processQueue = (error: unknown) => {
  failedQueue.forEach(({ resolve, reject, originalRequest }) => {
    if (error) {
      reject(error)
    } else {
      resolve(globalAxios(originalRequest))
    }
  })
  failedQueue = []
}

export const forceLogout = () => {
  if (hasLoggedOut) return
  hasLoggedOut = true
  authStore.handleAuthError()
  if (window.location.pathname !== "/login") {
    window.location.assign("/login")
  }
}

globalAxios.defaults.withCredentials = true

globalAxios.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetriableAxiosRequestConfig | undefined

    if (error.response?.status === 403) {
      return Promise.reject(error)
    }

    if (error.response?.status === 401) {
      if (isRefreshRequest(originalRequest)) {
        forceLogout()
        return Promise.reject(error)
      }

      if (originalRequest?._retry) {
        forceLogout()
        return Promise.reject(error)
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve,
            reject,
            originalRequest: originalRequest as AxiosRequestConfig,
          })
        }).catch((queueError) => {
          forceLogout()
          return Promise.reject(queueError)
        })
      }

      if (originalRequest) {
        originalRequest._retry = true
      }
      isRefreshing = true

      try {
        await new AuthenticationApi(apiConfig).refreshToken()
        processQueue(null)
        isRefreshing = false
        return globalAxios(originalRequest!)
      } catch (refreshError) {
        processQueue(refreshError)
        isRefreshing = false
        forceLogout()
        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  },
)

export const authenticationApi = new AuthenticationApi(apiConfig)
export const councilCustomerApi = new CouncilCustomerApi(apiConfig)
export const councilDeviceApi = new CouncilDeviceApi(apiConfig)
export const councilDashboardApi = new CouncilDashboardApi(apiConfig)
export const councilAlarmApi = new CouncilAlarmApi(apiConfig)
export const councilAdminApi = new CouncilAdminApi(apiConfig)
export const councilReportApi = new CouncilReportApi(apiConfig)
export const userProfileApi = new UserProfileApi(apiConfig)
export const councilApi = new CouncilApi(apiConfig)
export const adminDashboardApi = new AdminDashboardApi(apiConfig)
export const adminManagementApi = new AdminManagementApi(apiConfig)
export const councilAdminInvitations = new CouncilAdminInvitationsApi(apiConfig)
export const councilConsumptionApi = new CouncilConsumptionApi(apiConfig)
export const meterCatalogApi = new MeterCatalogControllerApi(apiConfig)
export const statisticsControllerApi = new StatisticsControllerApi(apiConfig)
export const superAdminAnalyticsControllerApi = new SuperAdminAnalyticsControllerApi(apiConfig)
export const superAdminCouncilApi = new SuperAdminCouncilControllerApi(apiConfig)
export const waterbureauRegistrationApi = new WaterbureauRegistrationApi(apiConfig)
export const councilAppVersionApi = new CouncilAppVersionApi(apiConfig)
